"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { MessageSquare, Phone, Mail, MapPin, Loader, ShieldCheck, Clock } from "lucide-react";
const inputStyles = "flex w-full rounded-md border border-border/50 bg-input/20 px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
          <ShieldCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/30 hover:bg-primary/10 hover:text-primary rounded-md">
          Send another inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-4">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Full Name</FormLabel>
              <FormControl>
                <input type="text" className={`${inputStyles} h-11 border-b-primary/30 focus-visible:ring-0 focus-visible:border-b-primary`} placeholder="Alexander Sterling" {...field} value={field.value || ''} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70">
                Please enter your name as it appears on professional documents.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/90 font-default">Email Address</FormLabel>
                <FormControl>
                  <input type="email" className={`${inputStyles} h-11 border-b-primary/30 focus-visible:ring-0 focus-visible:border-b-primary`} placeholder="alex@company.com" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/90 font-default">Phone Number</FormLabel>
                <FormControl>
                  <input type="tel" className={`${inputStyles} h-11 border-b-primary/30 focus-visible:ring-0 focus-visible:border-b-primary`} placeholder="+1 (555) 000-0000" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="subject" rules={{
        required: "Please select a service interest"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Service Interest</FormLabel>
              <FormControl>
                <select className={`${inputStyles} h-11 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236366f1%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_12px_center] bg-[length:16px_16px] pr-10 border-b-primary/30 focus-visible:ring-0 focus-visible:border-b-primary`} {...field} value={field.value || ''}>
                  <option value="" disabled>Select an area of interest</option>
                  {["Training & Skills Development", "Business Process Outsourcing (BPO)", "Corporate Strategy Consulting", "Custom Workforce Solutions", "Other Inquiry"].map((option, i) => <option key={i} value={option} data-index={i} className="bg-popover text-popover-foreground">
                      {option}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 20,
          message: "Message must be at least 20 characters"
        },
        maxLength: {
          value: 2000,
          message: "Message is too long"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Project Details</FormLabel>
              <FormControl>
                <textarea className={`${inputStyles} min-h-[120px] resize-y border-b-primary/30 focus-visible:ring-0 focus-visible:border-b-primary`} placeholder="Tell us about your training needs or BPO requirements..." {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-default font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-bold bg-gradient-to-r from-primary to-indigo-600 text-primary-foreground shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 rounded-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : 'Submit Inquiry'}
      </Button>
      
      <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1.5 font-sans">
        <ShieldCheck className="size-3.5 text-secondary" />
        Secure & confidential professional consultation.
      </p>
    </Form>;
}
export default function ContactezNosExperts() {
  return <section data-section-id="225030" id="lets-discuss-your-needs" className="bg-background text-foreground py-20 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/5 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-background/0 to-background/0 pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-md border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-default font-medium text-primary backdrop-blur-sm">
                <span className="flex size-1.5 rounded-lg bg-secondary mr-2 animate-pulse"></span>
                Response time: &lt; 24h
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tighter text-foreground">
                Let's Discuss Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Needs</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-md leading-relaxed font-sans">
                People first, performance that lasts. Since 2017, we have partnered to evolve workforces through targeted training. Today, we’re broadening that story to include BPO outsourcing, delivering scalable, outcomes-driven solutions that turn capability into real business impact.
              </p>
            </div>

            <div className="space-y-4 pt-4">
              {}
              <div className="group relative overflow-hidden rounded-lg border border-border/50 bg-card/40 p-6 backdrop-blur-xl transition-all hover:border-primary/50 hover:bg-card/60 hover:shadow-xl">
                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:scale-110 transition-transform duration-300">
                    <Phone className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-default text-lg font-bold text-foreground mb-1">Direct Consultation</h3>
                    <p className="text-sm text-muted-foreground mb-4 font-sans">Speak directly with our strategy leads for immediate project scoping.</p>
                    <Link to="/GetInTouchPage" className="inline-flex items-center text-sm font-default font-bold text-primary hover:text-primary/80 transition-colors">
                      Call our headquarters <span className="ml-1 transition-transform group-hover:translate-x-1">→</span>
                    </Link>
                  </div>
                </div>
              </div>

              {}
              <div className="rounded-lg border border-border/50 bg-card/40 p-6 backdrop-blur-xl shadow-md">
                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                    <MapPin className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-default text-lg font-bold text-foreground mb-1">Office Details</h3>
                    <ul className="text-sm text-muted-foreground space-y-2 mt-2 font-sans">
                      <li className="flex justify-between items-center border-b border-border/30 pb-1">
                        <span>Global Headquarters</span>
                        <span className="text-foreground/80 font-medium text-right ml-4">
                          Innovation Hub<br />
                          Long Ships (PTY) Ltd<br />
                          Arthur Tsengiwe College<br />
                          Queenstown Road<br />
                          Cala
                        </span>
                      </li>
                      <li className="flex justify-between items-center border-b border-border/30 pb-1">
                        <span>Business Hours</span>
                        <span className="text-foreground/80 font-medium">09:00 - 18:00 EST</span>
                      </li>
                      <li className="flex justify-between items-center">
                        <span>Email Support</span>
                        <span className="text-foreground/80 font-medium">admin@long-ships.co.za</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="relative rounded-lg border border-white/10 bg-background/40 p-6 sm:p-8 backdrop-blur-xl shadow-2xl overflow-hidden">
              {}
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-lg blur-[80px] pointer-events-none" />
              
              <div className="mb-8">
                <h3 className="text-2xl font-default font-bold text-foreground mb-2">Send a Request</h3>
                <p className="text-sm text-muted-foreground font-sans">Complete the form below and our BPO and Training specialists will reach out shortly.</p>
              </div>

              <FormProvider formKey="discuss_needs_get_in_touch" formId="discuss_needs_get_in_touch" sectionName="Let's Discuss Your Needs" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} submitText="Submit Inquiry" successMessage="Thank you for reaching out. A SKILLUP consultant will contact you within 24 hours to discuss your requirements." errorMessage="We encountered an issue processing your request. Please try again or contact us directly.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
      
      {}
      <Toaster position="bottom-right" />
    </section>;
}